<template>
  <form @submit.prevent="deleteEntity">
    <Stack
      class="stk-p-2"
      style="width: 32rem;"
      gap="4"
    >
      <Title>Delete Entity</Title>

      <LoaderWithText v-if="!hasFetchedPreview" />

      <template v-else>

        <div class="content mb-0">
          <p>
            Are you sure you want to delete
            <strong v-if="entity.name">{{ entity.name }}</strong>
            <span v-else>this person?</span>?
          </p>

          <template v-if="hasFetchedPreview">
            <Stack gap="3" class="stk-mt-2">
              <Callout status="info">
                <div class="stk-mb-2"><strong>Other advisors with view access</strong></div>
                <template v-if="advisorsWithViewAccess.length">
                  <ul>
                    <li v-for="(a, idx) in advisorsWithViewAccess"><small>{{ a.display || a.name || a.email }}</small></li>
                  </ul>
                </template>
                <template v-else>
                  <small>No other advisors currently have view access.</small>
                </template>
              </Callout>
            </Stack>

            <Stack gap="3" class="stk-mt-2">
              <Callout v-if="cascadeGroups.length" status="danger">
                <div class="stk-mb-2"><strong>Will be deleted</strong></div>
                <div v-for="(group, i) in cascadeGroups" :key="i" class="stk-mb-3">
                  <div class="stk-mb-1">
                    <strong>{{ group.label }}</strong>
                    <span class="stk-text-muted"> ({{ group.items.length }})</span>
                  </div>
                  <ul>
                    <li v-for="(item, k) in group.items" :key="k"><small>{{ item.display }}</small></li>
                  </ul>
                </div>
              </Callout>

              <Callout v-if="derefGroups.length" status="info">
                <div class="stk-mb-2"><strong>Will be disconnected</strong></div>
                <div v-for="(group, j) in derefGroups" :key="j" class="stk-mb-3">
                  <div class="stk-mb-1">
                    <strong>{{ group.label }}</strong>
                    <span class="stk-text-muted"> ({{ group.items.length }})</span>
                  </div>
                  <ul>
                    <li v-for="(item, l) in group.items" :key="l"><small>{{ item.display }}</small></li>
                  </ul>
                </div>
              </Callout>
            </Stack>
          </template>
        </div>

        <Callout
          v-if="hasFetchedPreview && deletePreview && deletePreview.protected && deletePreview.protected.length"
          status="danger"
        >
          Deletion is blocked by the following protected objects:
          <ul>
            <li v-for="(p, m) in deletePreview.protected" :key="m">{{ p.display }}</li>
          </ul>
        </Callout>

        <span>The SEC has regulations about the electronic storage of broker-dealer records. 
          <a
            href="https://www.sec.gov/rules-regulations/2003/05/electronic-storage-broker-dealer-records"
            target="_blank"
          > Read the details on SEC.gov.</a>
        </span>
          

        <Field>
          <Label>Type <strong>delete</strong> to confirm</Label>
          <input
            v-model="deleteInput"
            class="stk-input"
            type="text"
          >
        </Field>

        <Button
          variant="danger"
          :disabled="deleteInput.toLowerCase() !== 'delete' || hasDeletedEntity"
          :loading="isDeletingEntity"
        >
          Delete
          <strong v-if="entity.name">{{ entity.name }}</strong>
          <span v-else>this entity</span>
        </Button>
      </template>
    </Stack>
  </form>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { ToastProgrammatic as Toast } from 'buefy';
import { Button, Callout, Field, Label, LoaderWithText, Stack, Title } from '@stakeui';

const props = defineProps({
  entity: {
    type: Object,
    required: true,
  },
  entityExternalId: {
    type: String,
    required: true,
  },
  headers: {
    type: Object,
    required: true,
  },
});

const deletePreview = ref(null);
const hasFetchedPreview = ref(false);
const isFetchingPreview = ref(false);
const deleteInput = ref('');
const isDeletingEntity = ref(false);
const hasDeletedEntity = ref(false);

// Group preview children by cascade and model for display
const previewChildren = computed(() => {
  const root = deletePreview.value && deletePreview.value.root;
  return root && Array.isArray(root.children) ? root.children : [];
});

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const groupByModel = (items, cascade) => {
  const groups = {};
  for (const item of items) {
    const isCascade = !!item.cascade;
    if (cascade ? !isCascade : isCascade) continue;
    // Special-case grouping for child entities that are being disconnected
    let key;
    let labelOverride = null;
    if (item && item.reason === 'child_entity') {
      key = 'child_entities';
      labelOverride = 'Household relationships';
    } else {
      key = `${item.app_label}.${item.model}`;
    }
    if (!groups[key]) {
      groups[key] = {
        key,
        model: item.model,
        label: labelOverride || capitalize(item.verbose_name || item.model),
        items: [],
      };
    }
    groups[key].items.push(item);
  }
  const values = Object.values(groups);
  for (const g of values) {
    if (g && (g.model === 'dossier' || g.model === 'factsheet')) {
      g.items = g.items.slice(0, 1);
    }
  }
  return values.sort((a, b) => a.label.localeCompare(b.label));
};

// Partition into items that will be deleted (cascade/custom-delete) and disconnected
const deletedItems = computed(() => {
  const children = previewChildren.value || [];
  const cascChildren = children.filter((n) => n.on_delete === 'CASCADE' || n.on_delete === 'CUSTOM_DELETE' || n.cascade === true);
  const willBeDeleted = (deletePreview.value && Array.isArray(deletePreview.value.will_be_deleted))
    ? deletePreview.value.will_be_deleted
    : [];
  const seen = new Set();
  const key = (n) => `${n.app_label}:${n.model}:${n.pk}`;
  const merged = [];
  for (const n of [...willBeDeleted, ...cascChildren]) {
    const k = key(n);
    if (!seen.has(k)) {
      seen.add(k);
      merged.push(n);
    }
  }
  return merged;
});

const disconnectedItems = computed(() => {
  const children = previewChildren.value || [];
  const deletedKeys = new Set(
    (deletedItems.value || []).map((n) => `${n.app_label}:${n.model}:${n.pk}`)
  );
  return children.filter((n) => {
    const k = `${n.app_label}:${n.model}:${n.pk}`;
    const isDisconnect = n.on_delete === 'DISCONNECT' || n.cascade === false;
    // If an item is slated to be deleted, do not also show it as disconnected
    return isDisconnect && !deletedKeys.has(k);
  });
});

const cascadeGroups = computed(() => groupByModel(deletedItems.value, true));
const derefGroups = computed(() => groupByModel(disconnectedItems.value, false));

const advisorsWithViewAccess = computed(() => {
  const arr = deletePreview.value && Array.isArray(deletePreview.value.other_advisors_with_view_access)
    ? deletePreview.value.other_advisors_with_view_access
    : [];
  return arr;
});


const fetchDeletePreview = async () => {
  isFetchingPreview.value = true;

  try {
    const url = `/api/v1/entities/${props.entityExternalId}/delete-preview/`;
    const res = await fetch(url, {
      headers: props.headers,
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Fetching delete preview failed.');
    }

    const data = await res.json();
    deletePreview.value = data;
    hasFetchedPreview.value = true;
  } catch (e) {
    console.error('Error fetching delete preview:', e);
  } finally {
    isFetchingPreview.value = false;
  }
};

// No longer used nested list; keeping preview flat via grouped sections above

const deleteEntity = async () => {
  if (deleteInput.value.toLowerCase() !== 'delete') {
    return;
  }

  isDeletingEntity.value = true;

  try {
    const url = `/api/v1/entities/${props.entityExternalId}/delete/`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: props.headers,
    });

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Deletion failed.');
    }

    hasDeletedEntity.value = true;

    Toast.open({
      message: `Entity successfully deleted. Redirecting…`,
      type: 'is-success',
    });
    window.location.href = '/ui/advisor/people';
  } catch (e) {
    console.error('Error deleting the entity:', e);
  } finally {
    isDeletingEntity.value = false;
  }
};

onMounted(() => {
  fetchDeletePreview();
});
</script>

<style scoped>
</style>
